import React, { useMemo } from 'react';

import type { GlobalShortcutsItem, UseGlobalShortcutDataOutput } from '@atlassian/global-shortcuts';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';

import NavItemActions from '../../../common/ui/nav-item-actions';
import { useNavAnalytics } from '../../../common/utils/use-nav-analytics';

type UseGlobalShortcutsTransformationPropsType = {
	growthShortcutsConfig: UseGlobalShortcutDataOutput;
	l1DragAndDropEnabled?: boolean;
	shortcutsConfig: UseGlobalShortcutDataOutput;
	visibleShortcuts: string[];
};

type GlobalShortcutsConfigWithMenuId = GlobalShortcutsItem & { menuItemId: string };

/**
 * Creates a transformation utility for global shortcuts and drag and drop customization
 * by creating a mirrored structure of the original shortcuts configuration containing a
 * menu identifier reference that can be used for drag and drop operations.
 */
export const useGlobalShortcutsTransformation = ({
	shortcutsConfig,
	growthShortcutsConfig,
	visibleShortcuts,
	l1DragAndDropEnabled,
}: UseGlobalShortcutsTransformationPropsType) => {
	const {
		fireClickedSideNavItemEvent,
		fireClickedSideNavSubMenuItemEvent,
		fireViewedSideNavSubMenuEvent,
	} = useNavAnalytics();
	// 1. Filter the shortcut config based on visible menu items
	const shortcutsWithMenuId = useMemo(() => {
		const availableShortcuts = visibleShortcuts.reduce(
			(acc: GlobalShortcutsConfigWithMenuId[], id: string) => {
				const shortcut = shortcutsConfig?.[id];
				if (shortcut) {
					acc.push({ menuItemId: id, ...shortcut });
				}
				return acc;
			},
			[],
		);

		if (growthShortcutsConfig) {
			availableShortcuts.push(
				...Object.entries<GlobalShortcutsItem>(growthShortcutsConfig).map(([id, shortcut]) => ({
					menuItemId: id,
					...shortcut,
				})),
			);
		}
		return availableShortcuts.map((shortcut) => ({
			...shortcut,
			...(l1DragAndDropEnabled && {
				optionsMenuElem: (
					<NavItemActions itemMenuId={shortcut.menuItemId} group={L1_MENU_GROUPS.SHORTCUTS} />
				),
			}),
			onClick: () => {
				shortcut.onClick?.();
				fireClickedSideNavItemEvent(shortcut.menuItemId, 'postMain');
				if (shortcut.type === 'expand') {
					fireViewedSideNavSubMenuEvent(shortcut.menuItemId, undefined, false, 'postMain');
				}
			},
			...(shortcut.type === 'link' && {
				onRightClick: () => {
					shortcut.onRightClick?.();
					fireClickedSideNavItemEvent(shortcut.menuItemId, 'postMain', true);
				},
			}),
			// Fire analytics event for L2 items
			...(shortcut.type === 'expand' && {
				items: shortcut.items?.map((subItem) => ({
					...subItem,
					onClick: () => {
						subItem.onClick?.();
						fireClickedSideNavSubMenuItemEvent(
							shortcut.menuItemId,
							undefined,
							false,
							false,
							'postMain',
						);
					},
					onRightClick: () => {
						subItem.onRightClick?.();
						fireClickedSideNavSubMenuItemEvent(
							shortcut.menuItemId,
							undefined,
							false,
							true,
							'postMain',
						);
					},
				})),
			}),
		}));
	}, [
		visibleShortcuts,
		growthShortcutsConfig,
		shortcutsConfig,
		fireClickedSideNavItemEvent,
		fireViewedSideNavSubMenuEvent,
		fireClickedSideNavSubMenuItemEvent,
		l1DragAndDropEnabled,
	]);

	return {
		shortcuts: shortcutsWithMenuId.map(({ menuItemId, ...shortcut }) => shortcut),
		shortcutsWithMenuId: shortcutsWithMenuId,
	};
};
